import {
  Excalidraw,
  LiveCollaborationTrigger,
  TTDDialogTrigger,
  CaptureUpdateAction,
  reconcileElements,
  useEditorInterface,
  ExcalidrawAPIProvider,
  useExcalidrawAPI,
} from "@excalidraw/excalidraw";
import { trackEvent } from "@excalidraw/excalidraw/analytics";
import { getDefaultAppState } from "@excalidraw/excalidraw/appState";
import {
  CommandPalette,
  DEFAULT_CATEGORIES,
} from "@excalidraw/excalidraw/components/CommandPalette/CommandPalette";
import { ErrorDialog } from "@excalidraw/excalidraw/components/ErrorDialog";
import { OverwriteConfirmDialog } from "@excalidraw/excalidraw/components/OverwriteConfirm/OverwriteConfirm";
import { openConfirmModal } from "@excalidraw/excalidraw/components/OverwriteConfirm/OverwriteConfirmState";
import { ShareableLinkDialog } from "@excalidraw/excalidraw/components/ShareableLinkDialog";
import Trans from "@excalidraw/excalidraw/components/Trans";
import {
  APP_NAME,
  EVENT,
  THEME,
  VERSION_TIMEOUT,
  debounce,
  getVersion,
  getFrame,
  isTestEnv,
  preventUnload,
  resolvablePromise,
  isRunningInIframe,
  isDevEnv,
} from "@excalidraw/common";
import polyfill from "@excalidraw/excalidraw/polyfill";
import { useCallback, useEffect, useRef, useState } from "react";
import { loadFromBlob } from "@excalidraw/excalidraw/data/blob";
import { t } from "@excalidraw/excalidraw/i18n";

import {
  GithubIcon,
  XBrandIcon,
  DiscordIcon,
  ExcalLogo,
  usersIcon,
  exportToPlus,
  share,
  youtubeIcon,
} from "@excalidraw/excalidraw/components/icons";
import { isElementLink } from "@excalidraw/element";
import {
  bumpElementVersions,
  restoreAppState,
  restoreElements,
} from "@excalidraw/excalidraw/data/restore";
import { newElementWith } from "@excalidraw/element";
import { isInitializedImageElement } from "@excalidraw/element";
import clsx from "clsx";
import {
  parseLibraryTokensFromUrl,
  useHandleLibrary,
} from "@excalidraw/excalidraw/data/library";

import type { RemoteExcalidrawElement } from "@excalidraw/excalidraw/data/reconcile";
import type { RestoredDataState } from "@excalidraw/excalidraw/data/restore";
import type {
  FileId,
  NonDeletedExcalidrawElement,
  OrderedExcalidrawElement,
} from "@excalidraw/element/types";
import type {
  AppState,
  ExcalidrawImperativeAPI,
  BinaryFiles,
  ExcalidrawInitialDataState,
  UIAppState,
  ExcalidrawProps,
} from "@excalidraw/excalidraw/types";
import type { ResolutionType } from "@excalidraw/common/utility-types";
import type { ResolvablePromise } from "@excalidraw/common/utils";

import CustomStats from "./CustomStats";
import {
  Provider,
  useAtom,
  useAtomValue,
  useAtomWithInitialValue,
  appJotaiStore,
} from "./app-jotai";
import {
  FIREBASE_STORAGE_PREFIXES,
  isExcalidrawPlusSignedUser,
  STORAGE_KEYS,
  SYNC_BROWSER_TABS_TIMEOUT,
} from "./app_constants";
import Collab, {
  collabAPIAtom,
  isCollaboratingAtom,
  isOfflineAtom,
} from "./collab/Collab";
import { AppFooter } from "./components/AppFooter";
import { AppMainMenu } from "./components/AppMainMenu";
import { AppWelcomeScreen } from "./components/AppWelcomeScreen";
import {
  ExportToExcalidrawPlus,
  exportToExcalidrawPlus,
} from "./components/ExportToExcalidrawPlus";
import { TopErrorBoundary } from "./components/TopErrorBoundary";
import { UserSessionDialog } from "./components/UserSessionDialog";
import { WorkFilesDialog } from "./components/WorkFilesDialog";

import {
  exportToBackend,
  getCollaborationLinkData,
  importFromBackend,
  isCollaborationLink,
} from "./data";

import { updateStaleImageStatuses } from "./data/FileManager";
import { FileStatusStore } from "./data/fileStatusStore";
import {
  importFromLocalStorage,
  importUsernameFromLocalStorage,
} from "./data/localStorage";

import { loadFilesFromFirebase } from "./data/firebase";
import {
  LibraryIndexedDBAdapter,
  LibraryLocalStorageMigrationAdapter,
  LocalData,
  localStorageQuotaExceededAtom,
} from "./data/LocalData";
import { isBrowserStorageStateNewer } from "./data/tabSync";
import {
  DEFAULT_WORKFILE_ID,
  createWorkFile,
  deleteWorkFile,
  getActiveWorkFileId,
  getWorkFiles,
  getWorkFileStorageKeys,
  renameWorkFile,
  setActiveWorkFileId as setActiveSessionWorkFileId,
  type WorkFile,
} from "./data/workfiles";
import { getSessionUser, type SessionUser } from "./data/auth";
import {
  createRemoteWorkFile,
  deleteRemoteWorkFile,
  listRemoteWorkFiles,
  loadRemoteWorkFile,
  renameRemoteWorkFile,
  saveRemoteWorkFile,
} from "./data/remoteWorkfiles";
import { ShareDialog, shareDialogStateAtom } from "./share/ShareDialog";
import CollabError, { collabErrorIndicatorAtom } from "./collab/CollabError";
import { useHandleAppTheme } from "./useHandleAppTheme";
import { getPreferredLanguage } from "./app-language/language-detector";
import { useAppLangCode } from "./app-language/language-state";
import DebugCanvas, {
  debugRenderer,
  isVisualDebuggerEnabled,
  loadSavedDebugState,
} from "./components/DebugCanvas";
import { AIComponents } from "./components/AI";
import { ExcalidrawPlusIframeExport } from "./ExcalidrawPlusIframeExport";

import "./index.scss";

import { ExcalidrawPlusPromoBanner } from "./components/ExcalidrawPlusPromoBanner";
import { AppSidebar } from "./components/AppSidebar";

import type { CollabAPI } from "./collab/Collab";

polyfill();

window.EXCALIDRAW_THROTTLE_RENDER = true;

declare global {
  interface BeforeInstallPromptEventChoiceResult {
    outcome: "accepted" | "dismissed";
  }

  interface BeforeInstallPromptEvent extends Event {
    prompt(): Promise<void>;
    userChoice: Promise<BeforeInstallPromptEventChoiceResult>;
  }

  interface WindowEventMap {
    beforeinstallprompt: BeforeInstallPromptEvent;
  }
}

let pwaEvent: BeforeInstallPromptEvent | null = null;

// Adding a listener outside of the component as it may (?) need to be
// subscribed early to catch the event.
//
// Also note that it will fire only if certain heuristics are met (user has
// used the app for some time, etc.)
window.addEventListener(
  "beforeinstallprompt",
  (event: BeforeInstallPromptEvent) => {
    // prevent Chrome <= 67 from automatically showing the prompt
    event.preventDefault();
    // cache for later use
    pwaEvent = event;
  },
);

let isSelfEmbedding = false;

if (window.self !== window.top) {
  try {
    const parentUrl = new URL(document.referrer);
    const currentUrl = new URL(window.location.href);
    if (parentUrl.origin === currentUrl.origin) {
      isSelfEmbedding = true;
    }
  } catch (error) {
    // ignore
  }
}

const shareableLinkConfirmDialog = {
  title: t("overwriteConfirm.modal.shareableLink.title"),
  description: (
    <Trans
      i18nKey="overwriteConfirm.modal.shareableLink.description"
      bold={(text) => <strong>{text}</strong>}
      br={() => <br />}
    />
  ),
  actionLabel: t("overwriteConfirm.modal.shareableLink.button"),
  color: "danger",
} as const;

const initializeScene = async (opts: {
  collabAPI: CollabAPI | null;
  excalidrawAPI: ExcalidrawImperativeAPI;
}): Promise<
  { scene: ExcalidrawInitialDataState | null } & (
    | { isExternalScene: true; id: string; key: string }
    | { isExternalScene: false; id?: null; key?: null }
  )
> => {
  const searchParams = new URLSearchParams(window.location.search);
  const id = searchParams.get("id");
  const jsonBackendMatch = window.location.hash.match(
    /^#json=([a-zA-Z0-9_-]+),([a-zA-Z0-9_-]+)$/,
  );
  const externalUrlMatch = window.location.hash.match(/^#url=(.*)$/);

  const localDataState = importFromLocalStorage();

  let scene: Omit<
    RestoredDataState,
    // we're not storing files in the scene database/localStorage, and instead
    // fetch them async from a different store
    "files"
  > & {
    scrollToContent?: boolean;
  } = {
    elements: restoreElements(localDataState?.elements, null, {
      repairBindings: true,
      deleteInvisibleElements: true,
    }),
    appState: restoreAppState(localDataState?.appState, null),
  };

  let roomLinkData = getCollaborationLinkData(window.location.href);
  const isExternalScene = !!(id || jsonBackendMatch || roomLinkData);
  if (isExternalScene) {
    if (
      // don't prompt if scene is empty
      !scene.elements.length ||
      // don't prompt for collab scenes because we don't override local storage
      roomLinkData ||
      // otherwise, prompt whether user wants to override current scene
      (await openConfirmModal(shareableLinkConfirmDialog))
    ) {
      if (jsonBackendMatch) {
        const imported = await importFromBackend(
          jsonBackendMatch[1],
          jsonBackendMatch[2],
        );

        scene = {
          elements: bumpElementVersions(
            restoreElements(imported.elements, null, {
              repairBindings: true,
              deleteInvisibleElements: true,
            }),
            localDataState?.elements,
          ),
          appState: restoreAppState(
            imported.appState,
            // local appState when importing from backend to ensure we restore
            // localStorage user settings which we do not persist on server.
            localDataState?.appState,
          ),
        };
      }
      scene.scrollToContent = true;
      if (!roomLinkData) {
        window.history.replaceState({}, APP_NAME, window.location.origin);
      }
    } else {
      // https://github.com/excalidraw/excalidraw/issues/1919
      if (document.hidden) {
        return new Promise((resolve, reject) => {
          window.addEventListener(
            "focus",
            () => initializeScene(opts).then(resolve).catch(reject),
            {
              once: true,
            },
          );
        });
      }

      roomLinkData = null;
      window.history.replaceState({}, APP_NAME, window.location.origin);
    }
  } else if (externalUrlMatch) {
    window.history.replaceState({}, APP_NAME, window.location.origin);

    const url = externalUrlMatch[1];
    try {
      const request = await fetch(window.decodeURIComponent(url));
      const data = await loadFromBlob(await request.blob(), null, null);
      if (
        !scene.elements.length ||
        (await openConfirmModal(shareableLinkConfirmDialog))
      ) {
        return { scene: data, isExternalScene };
      }
    } catch (error: any) {
      return {
        scene: {
          appState: {
            errorMessage: t("alerts.invalidSceneUrl"),
          },
        },
        isExternalScene,
      };
    }
  }

  if (roomLinkData && opts.collabAPI) {
    const { excalidrawAPI } = opts;

    const scene = await opts.collabAPI.startCollaboration(roomLinkData);

    // Avoid building a synthetic scene when the socket is already open (e.g.
    // duplicate init from effect re-runs); reconciling against an empty canvas
    // can win the race and resolve initialData incorrectly.
    if (
      scene === null &&
      opts.collabAPI.isCollaborating() &&
      isCollaborationLink(window.location.href)
    ) {
      return {
        scene: null,
        isExternalScene: true,
        id: roomLinkData.roomId,
        key: roomLinkData.roomKey,
      };
    }

    return {
      // when collaborating, the state may have already been updated at this
      // point (we may have received updates from other clients), so reconcile
      // elements and appState with existing state
      scene: {
        ...scene,
        appState: {
          ...restoreAppState(
            {
              ...scene?.appState,
              theme: localDataState?.appState?.theme || scene?.appState?.theme,
            },
            excalidrawAPI.getAppState(),
          ),
          // necessary if we're invoking from a hashchange handler which doesn't
          // go through App.initializeScene() that resets this flag
          isLoading: false,
        },
        elements: reconcileElements(
          scene?.elements || [],
          excalidrawAPI.getSceneElementsIncludingDeleted() as RemoteExcalidrawElement[],
          excalidrawAPI.getAppState(),
        ),
      },
      isExternalScene: true,
      id: roomLinkData.roomId,
      key: roomLinkData.roomKey,
    };
  } else if (scene) {
    return isExternalScene && jsonBackendMatch
      ? {
          scene,
          isExternalScene,
          id: jsonBackendMatch[1],
          key: jsonBackendMatch[2],
        }
      : { scene, isExternalScene: false };
  }
  return { scene: null, isExternalScene: false };
};

const REMOTE_ACTIVE_WORKFILE_STORAGE_KEY = "excalidraw:remote-active-workfile";

const getRemoteActiveWorkFileKey = (userId: string) => {
  return `${REMOTE_ACTIVE_WORKFILE_STORAGE_KEY}:${userId}`;
};

const getStoredRemoteActiveWorkFileId = (userId: string) => {
  try {
    return window.sessionStorage.getItem(getRemoteActiveWorkFileKey(userId));
  } catch (error) {
    console.error(error);
    return null;
  }
};

const setStoredRemoteActiveWorkFileId = (
  userId: string,
  workFileId: string,
) => {
  try {
    window.sessionStorage.setItem(
      getRemoteActiveWorkFileKey(userId),
      workFileId,
    );
  } catch (error) {
    console.error(error);
  }
};

const sortWorkFiles = <T extends { updatedAt: number }>(
  workFiles: readonly T[],
) => {
  return [...workFiles].sort((left, right) => right.updatedAt - left.updatedAt);
};

const upsertWorkFile = (workFiles: readonly WorkFile[], workFile: WorkFile) => {
  const nextWorkFiles = workFiles.filter(
    (currentWorkFile) => currentWorkFile.id !== workFile.id,
  );
  nextWorkFiles.push(workFile);
  return sortWorkFiles(nextWorkFiles);
};

const getSceneAppStateWithWorkFileName = (
  appState: Partial<AppState> | null,
  workFileName: string | null,
) => {
  if (!workFileName) {
    return appState;
  }

  if (!appState) {
    return { name: workFileName };
  }

  if (appState.name?.trim()) {
    return appState;
  }

  return {
    ...appState,
    name: workFileName,
  };
};

const ExcalidrawWrapper = () => {
  const excalidrawAPI = useExcalidrawAPI();

  const [errorMessage, setErrorMessage] = useState("");
  const isCollabDisabled = isRunningInIframe();

  const { editorTheme, appTheme, setAppTheme } = useHandleAppTheme();

  const [langCode, setLangCode] = useAppLangCode();

  const editorInterface = useEditorInterface();

  // initial state
  // ---------------------------------------------------------------------------

  const initialStatePromiseRef = useRef<{
    promise: ResolvablePromise<ExcalidrawInitialDataState | null>;
  }>({ promise: null! });
  if (!initialStatePromiseRef.current.promise) {
    initialStatePromiseRef.current.promise =
      resolvablePromise<ExcalidrawInitialDataState | null>();
  }

  const initialSceneResolvedRef = useRef(false);

  const debugCanvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    trackEvent("load", "frame", getFrame());
    // Delayed so that the app has a time to load the latest SW
    setTimeout(() => {
      trackEvent("load", "version", getVersion());
    }, VERSION_TIMEOUT);
  }, []);

  const [, setShareDialogState] = useAtom(shareDialogStateAtom);
  const [collabAPI] = useAtom(collabAPIAtom);
  const [isCollaborating] = useAtomWithInitialValue(isCollaboratingAtom, () => {
    return isCollaborationLink(window.location.href);
  });
  const collabError = useAtomValue(collabErrorIndicatorAtom);

  useHandleLibrary({
    excalidrawAPI,
    adapter: LibraryIndexedDBAdapter,
    // TODO maybe remove this in several months (shipped: 24-03-11)
    migrationAdapter: LibraryLocalStorageMigrationAdapter,
  });

  const [, forceRefresh] = useState(false);
  const [authUser, setAuthUser] = useState<SessionUser | null>(null);
  const [isAuthDialogOpen, setIsAuthDialogOpen] = useState(false);
  const [activeWorkFileId, setActiveWorkFileId] = useState(() =>
    getActiveWorkFileId(),
  );
  const [isWorkFilesDialogOpen, setIsWorkFilesDialogOpen] = useState(false);
  const [workFiles, setWorkFiles] = useState(() => getWorkFiles());
  const [latestShareableLink, setLatestShareableLink] = useState<string | null>(
    null,
  );
  const lastWorkFileModeRef = useRef<"local" | "remote">("local");
  const authTransitionModeRef = useRef<"register" | "login" | null>(null);
  const isRemoteMode = !!authUser;

  const refreshLocalWorkFiles = useCallback(() => {
    setActiveWorkFileId(getActiveWorkFileId());
    setWorkFiles(getWorkFiles());
  }, []);

  const refreshRemoteWorkFiles = useCallback(async () => {
    if (!authUser) {
      return [];
    }

    const remoteWorkFiles = await listRemoteWorkFiles();
    const storedWorkFileId = getStoredRemoteActiveWorkFileId(authUser.id);

    setWorkFiles(remoteWorkFiles);
    setActiveWorkFileId((currentWorkFileId) => {
      if (
        remoteWorkFiles.some((workFile) => workFile.id === currentWorkFileId)
      ) {
        return currentWorkFileId;
      }

      if (
        storedWorkFileId &&
        remoteWorkFiles.some((workFile) => workFile.id === storedWorkFileId)
      ) {
        return storedWorkFileId;
      }

      return remoteWorkFiles[0]?.id || DEFAULT_WORKFILE_ID;
    });

    return remoteWorkFiles;
  }, [authUser]);

  const refreshWorkFiles = useCallback(async () => {
    if (authUser) {
      return refreshRemoteWorkFiles();
    }

    refreshLocalWorkFiles();
    return getWorkFiles();
  }, [authUser, refreshLocalWorkFiles, refreshRemoteWorkFiles]);

  useEffect(() => {
    if (isDevEnv()) {
      const debugState = loadSavedDebugState();

      if (debugState.enabled && !window.visualDebug) {
        window.visualDebug = {
          data: [],
        };
      } else {
        delete window.visualDebug;
      }
      forceRefresh((prev) => !prev);
    }
  }, [excalidrawAPI]);

  useEffect(() => {
    let isCancelled = false;

    getSessionUser()
      .then((user) => {
        if (!isCancelled) {
          setAuthUser(user);
        }
      })
      .catch((error) => {
        console.error(error);
      });

    return () => {
      isCancelled = true;
    };
  }, []);

  // ---------------------------------------------------------------------------
  // Hoisted loadImages
  // ---------------------------------------------------------------------------
  const loadImages = useCallback(
    (data: ResolutionType<typeof initializeScene>, isInitialLoad = false) => {
      if (!data.scene || !excalidrawAPI) {
        return;
      }

      if (collabAPI?.isCollaborating()) {
        if (data.scene.elements) {
          collabAPI
            .fetchImageFilesFromFirebase({
              elements: data.scene.elements,
              forceFetchFiles: true,
            })
            .then(({ loadedFiles, erroredFiles }) => {
              excalidrawAPI.addFiles(loadedFiles);
              updateStaleImageStatuses({
                excalidrawAPI,
                erroredFiles,
                elements: excalidrawAPI.getSceneElementsIncludingDeleted(),
              });
            });
        }
      } else {
        const fileIds =
          data.scene.elements?.reduce((acc, element) => {
            if (isInitializedImageElement(element)) {
              return acc.concat(element.fileId);
            }
            return acc;
          }, [] as FileId[]) || [];

        if (data.isExternalScene) {
          if (fileIds.length) {
            // Direct Firebase call (not through FileManager), so track manually
            FileStatusStore.updateStatuses(
              fileIds.map((id) => [id, "loading"]),
            );
          }
          loadFilesFromFirebase(
            `${FIREBASE_STORAGE_PREFIXES.shareLinkFiles}/${data.id}`,
            data.key,
            fileIds,
          ).then(({ loadedFiles, erroredFiles }) => {
            excalidrawAPI.addFiles(loadedFiles);
            updateStaleImageStatuses({
              excalidrawAPI,
              erroredFiles,
              elements: excalidrawAPI.getSceneElementsIncludingDeleted(),
            });
            FileStatusStore.updateStatuses([
              ...loadedFiles.map((f) => [f.id, "loaded"] as [FileId, "loaded"]),
              ...[...erroredFiles.keys()].map(
                (id) => [id, "error"] as [FileId, "error"],
              ),
            ]);
          });
        } else if (isInitialLoad) {
          if (fileIds.length) {
            LocalData.fileStorage
              .getFiles(fileIds)
              .then(async ({ loadedFiles, erroredFiles }) => {
                if (loadedFiles.length) {
                  excalidrawAPI.addFiles(loadedFiles);
                }
                updateStaleImageStatuses({
                  excalidrawAPI,
                  erroredFiles,
                  elements: excalidrawAPI.getSceneElementsIncludingDeleted(),
                });
              });
          }
          // on fresh load, clear unused files from IDB (from previous
          // session)
          LocalData.fileStorage.clearObsoleteFiles({
            currentFileIds: fileIds,
          });
        }
      }
    },
    [collabAPI, excalidrawAPI],
  );

  const resetExternalContext = useCallback(() => {
    if (collabAPI?.isCollaborating()) {
      collabAPI.stopCollaboration(false);
    }

    const url = new URL(window.location.href);
    url.searchParams.delete("id");
    url.hash = "";
    window.history.replaceState({}, APP_NAME, `${url.pathname}${url.search}`);

    setLatestShareableLink(null);
    setShareDialogState({ isOpen: false });
  }, [collabAPI, setShareDialogState]);

  const switchToLocalWorkFile = useCallback(
    (
      workFileId: string,
      opts?: { force?: boolean; keepDialogOpen?: boolean },
    ) => {
      if (!excalidrawAPI) {
        return;
      }

      if (!opts?.force && workFileId === getActiveWorkFileId()) {
        if (!opts?.keepDialogOpen) {
          setIsWorkFilesDialogOpen(false);
        }
        refreshLocalWorkFiles();
        return;
      }

      LocalData.flushSave();
      resetExternalContext();
      setActiveSessionWorkFileId(workFileId);

      const localDataState = importFromLocalStorage(workFileId);
      const currentWorkFile =
        getWorkFiles().find((workFile) => workFile.id === workFileId) || null;

      excalidrawAPI.resetScene({ resetLoadingState: true });
      excalidrawAPI.updateScene({
        elements: restoreElements(localDataState.elements, null, {
          repairBindings: true,
          deleteInvisibleElements: true,
        }),
        appState: restoreAppState(
          getSceneAppStateWithWorkFileName(
            localDataState.appState,
            currentWorkFile?.name || null,
          ),
          null,
        ),
        captureUpdate: CaptureUpdateAction.NEVER,
      });

      loadImages(
        {
          scene: localDataState,
          isExternalScene: false,
        },
        true,
      );

      setActiveWorkFileId(workFileId);
      lastWorkFileModeRef.current = "local";

      if (!opts?.keepDialogOpen) {
        setIsWorkFilesDialogOpen(false);
      }
      refreshLocalWorkFiles();
    },
    [excalidrawAPI, loadImages, refreshLocalWorkFiles, resetExternalContext],
  );

  const switchToRemoteWorkFile = useCallback(
    async (
      workFileId: string,
      opts?: { force?: boolean; keepDialogOpen?: boolean },
    ) => {
      if (!excalidrawAPI || !authUser) {
        return;
      }

      if (!opts?.force && workFileId === activeWorkFileId) {
        if (!opts?.keepDialogOpen) {
          setIsWorkFilesDialogOpen(false);
        }
        await refreshRemoteWorkFiles();
        return;
      }

      // Persist any pending edits to the *current* work file before we load
      // and apply another one. `flushSave` runs while `activeWorkFileId` still
      // points at the outgoing file, so the flushed save carries its id.
      LocalData.flushSave();

      // Pause saving while we swap the scene: applying the loaded scene fires
      // `onChange`, which would otherwise schedule a save before
      // `activeWorkFileId` has been updated, persisting the incoming scene
      // under the outgoing work file id.
      LocalData.pauseSave("workFileSwitch");

      try {
        resetExternalContext();

        const { scene, workFile } = await loadRemoteWorkFile(workFileId);
        const sceneAppState = getSceneAppStateWithWorkFileName(
          scene.appState,
          workFile.name,
        );

        // Update the ref synchronously so any save scheduled right after the
        // switch is attributed to the incoming work file.
        activeWorkFileIdRef.current = workFileId;

        excalidrawAPI.resetScene({ resetLoadingState: true });
        excalidrawAPI.updateScene({
          elements: restoreElements(scene.elements, null, {
            repairBindings: true,
            deleteInvisibleElements: true,
          }),
          appState: restoreAppState(sceneAppState, null),
          captureUpdate: CaptureUpdateAction.NEVER,
        });

        const files = Object.values(scene.files || {});
        if (files.length) {
          excalidrawAPI.addFiles(files);
        }

        setStoredRemoteActiveWorkFileId(authUser.id, workFileId);
        setActiveWorkFileId(workFileId);
        setWorkFiles((currentWorkFiles) =>
          upsertWorkFile(currentWorkFiles, workFile),
        );
        lastWorkFileModeRef.current = "remote";

        if (!opts?.keepDialogOpen) {
          setIsWorkFilesDialogOpen(false);
        }
      } catch (error) {
        console.error(error);
        setErrorMessage(
          error instanceof Error ? error.message : "Request failed",
        );
      } finally {
        LocalData.resumeSave("workFileSwitch");
      }
    },
    [
      activeWorkFileId,
      authUser,
      excalidrawAPI,
      refreshRemoteWorkFiles,
      resetExternalContext,
    ],
  );

  const authUserRef = useRef(authUser);
  const activeWorkFileIdRef = useRef(activeWorkFileId);
  const switchToRemoteWorkFileRef = useRef(switchToRemoteWorkFile);

  useEffect(() => {
    authUserRef.current = authUser;
    activeWorkFileIdRef.current = activeWorkFileId;
    switchToRemoteWorkFileRef.current = switchToRemoteWorkFile;
  }, [authUser, activeWorkFileId, switchToRemoteWorkFile]);

  const switchToWorkFile = useCallback(
    async (
      workFileId: string,
      opts?: { force?: boolean; keepDialogOpen?: boolean },
    ) => {
      if (authUser) {
        await switchToRemoteWorkFile(workFileId, opts);
        return;
      }

      switchToLocalWorkFile(workFileId, opts);
    },
    [authUser, switchToLocalWorkFile, switchToRemoteWorkFile],
  );

  const createAndSwitchToWorkFile = useCallback(
    async (name: string) => {
      if (authUser) {
        try {
          const workFile = await createRemoteWorkFile(name);
          setWorkFiles((currentWorkFiles) =>
            upsertWorkFile(currentWorkFiles, workFile),
          );
          await switchToRemoteWorkFile(workFile.id, { force: true });
        } catch (error) {
          console.error(error);
          setErrorMessage(
            error instanceof Error ? error.message : "Request failed",
          );
        }
        return;
      }

      const workFile = createWorkFile({ name });
      switchToLocalWorkFile(workFile.id);
    },
    [authUser, switchToLocalWorkFile, switchToRemoteWorkFile],
  );

  const renameAndRefreshWorkFile = useCallback(
    async (workFileId: string, name: string) => {
      if (authUser) {
        try {
          const workFile = await renameRemoteWorkFile(workFileId, name);
          setWorkFiles((currentWorkFiles) =>
            upsertWorkFile(currentWorkFiles, workFile),
          );

          if (workFileId === activeWorkFileId && excalidrawAPI) {
            excalidrawAPI.updateScene({
              appState: {
                name: workFile.name,
              },
              captureUpdate: CaptureUpdateAction.EVENTUALLY,
            });
          }
        } catch (error) {
          console.error(error);
          setErrorMessage(
            error instanceof Error ? error.message : "Request failed",
          );
        }
        return;
      }

      const workFile = renameWorkFile(workFileId, { name });

      if (workFileId === activeWorkFileId && excalidrawAPI) {
        excalidrawAPI.updateScene({
          appState: {
            name: workFile.name,
          },
          captureUpdate: CaptureUpdateAction.EVENTUALLY,
        });
      }

      refreshLocalWorkFiles();
    },
    [activeWorkFileId, authUser, excalidrawAPI, refreshLocalWorkFiles],
  );

  const deleteAndRefreshWorkFile = useCallback(
    async (workFileId: string) => {
      const nextWorkFileId =
        workFiles.find((workFile) => workFile.id !== workFileId)?.id ||
        DEFAULT_WORKFILE_ID;

      if (authUser) {
        try {
          const nextWorkFiles = await deleteRemoteWorkFile(workFileId);
          setWorkFiles(nextWorkFiles);

          if (workFileId === activeWorkFileId) {
            const fallbackWorkFileId =
              nextWorkFiles.find((workFile) => workFile.id !== workFileId)
                ?.id || DEFAULT_WORKFILE_ID;
            await switchToRemoteWorkFile(fallbackWorkFileId, {
              force: true,
              keepDialogOpen: true,
            });
          }
        } catch (error) {
          console.error(error);
          setErrorMessage(
            error instanceof Error ? error.message : "Request failed",
          );
        }
        return;
      }

      deleteWorkFile(workFileId);

      if (workFileId === activeWorkFileId) {
        switchToLocalWorkFile(nextWorkFileId, {
          force: true,
          keepDialogOpen: true,
        });
        return;
      }

      refreshLocalWorkFiles();
    },
    [
      activeWorkFileId,
      authUser,
      refreshLocalWorkFiles,
      switchToLocalWorkFile,
      switchToRemoteWorkFile,
      workFiles,
    ],
  );

  useEffect(() => {
    if (!authUser) {
      LocalData.setExternalSaveHandler(null);
      return;
    }

    LocalData.setExternalSaveHandler(
      async ({ elements, appState, files, workFileId }) => {
        try {
          const workFile = await saveRemoteWorkFile(workFileId, {
            elements,
            appState,
            files,
          });

          if (workFile) {
            setWorkFiles((currentWorkFiles) =>
              upsertWorkFile(currentWorkFiles, workFile),
            );
          }
        } catch (error) {
          console.error(error);
          setErrorMessage(
            error instanceof Error ? error.message : "Request failed",
          );
        }
      },
    );

    return () => {
      LocalData.setExternalSaveHandler(null);
    };
  }, [authUser]);

  useEffect(() => {
    if (!excalidrawAPI) {
      return;
    }

    let isCancelled = false;

    if (!authUser) {
      if (lastWorkFileModeRef.current === "remote") {
        switchToLocalWorkFile(getActiveWorkFileId(), {
          force: true,
          keepDialogOpen: true,
        });
      } else {
        refreshLocalWorkFiles();
      }

      lastWorkFileModeRef.current = "local";
      return;
    }

    const syncRemoteState = async () => {
      try {
        const remoteWorkFiles = await listRemoteWorkFiles();
        if (isCancelled) {
          return;
        }

        const storedWorkFileId = getStoredRemoteActiveWorkFileId(authUser.id);
        const initialWorkFileId =
          (storedWorkFileId &&
            remoteWorkFiles.some(
              (workFile) => workFile.id === storedWorkFileId,
            ) &&
            storedWorkFileId) ||
          remoteWorkFiles[0]?.id ||
          DEFAULT_WORKFILE_ID;

        const hasActiveCollaborationContext =
          !!collabAPI?.isCollaborating() ||
          isCollaborationLink(window.location.href);

        if (hasActiveCollaborationContext) {
          setWorkFiles(remoteWorkFiles);
          setActiveWorkFileId(initialWorkFileId);
          lastWorkFileModeRef.current = "remote";
          authTransitionModeRef.current = null;
          return;
        }

        if (authTransitionModeRef.current === "register") {
          const workFile = await saveRemoteWorkFile(DEFAULT_WORKFILE_ID, {
            elements: excalidrawAPI.getSceneElementsIncludingDeleted(),
            appState: excalidrawAPI.getAppState(),
            files: excalidrawAPI.getFiles(),
          });

          setStoredRemoteActiveWorkFileId(authUser.id, DEFAULT_WORKFILE_ID);
          setActiveWorkFileId(DEFAULT_WORKFILE_ID);
          if (workFile) {
            setWorkFiles(upsertWorkFile(remoteWorkFiles, workFile));
          } else {
            setWorkFiles(remoteWorkFiles);
          }
          lastWorkFileModeRef.current = "remote";
          authTransitionModeRef.current = null;
          return;
        }

        setWorkFiles(remoteWorkFiles);
        setActiveWorkFileId(initialWorkFileId);
        await switchToRemoteWorkFile(initialWorkFileId, {
          force: true,
          keepDialogOpen: true,
        });
        authTransitionModeRef.current = null;
      } catch (error) {
        console.error(error);
        if (!isCancelled) {
          setErrorMessage(
            error instanceof Error ? error.message : "Request failed",
          );
        }
      }
    };

    syncRemoteState();

    return () => {
      isCancelled = true;
    };
  }, [
    authUser,
    collabAPI,
    excalidrawAPI,
    refreshLocalWorkFiles,
    switchToLocalWorkFile,
    switchToRemoteWorkFile,
  ]);

  useEffect(() => {
    if (!excalidrawAPI || (!isCollabDisabled && !collabAPI)) {
      return;
    }

    initializeScene({ collabAPI, excalidrawAPI }).then(async (data) => {
      const skipDuplicateCollabInit =
        data.scene === null &&
        isCollaborationLink(window.location.href) &&
        collabAPI?.isCollaborating();

      if (data.scene !== null) {
        loadImages(data, /* isInitialLoad */ true);
      }

      if (skipDuplicateCollabInit) {
        return;
      }

      if (!initialSceneResolvedRef.current) {
        initialSceneResolvedRef.current = true;
        initialStatePromiseRef.current.promise.resolve(data.scene);
      }
    });

    const onHashChange = async (event: HashChangeEvent) => {
      event.preventDefault();
      const libraryUrlTokens = parseLibraryTokensFromUrl();
      if (!libraryUrlTokens) {
        if (
          collabAPI?.isCollaborating() &&
          !isCollaborationLink(window.location.href)
        ) {
          collabAPI.stopCollaboration(false);
        }
        excalidrawAPI.updateScene({ appState: { isLoading: true } });

        if (authUserRef.current && !isCollaborationLink(window.location.href)) {
          await switchToRemoteWorkFileRef.current(activeWorkFileIdRef.current, {
            force: true,
            keepDialogOpen: true,
          });
          return;
        }

        initializeScene({ collabAPI, excalidrawAPI }).then((data) => {
          if (
            data.scene === null &&
            isCollaborationLink(window.location.href) &&
            collabAPI?.isCollaborating()
          ) {
            return;
          }
          loadImages(data);
          if (data.scene) {
            excalidrawAPI.updateScene({
              elements: restoreElements(data.scene.elements, null, {
                repairBindings: true,
              }),
              appState: restoreAppState(data.scene.appState, null),
              captureUpdate: CaptureUpdateAction.IMMEDIATELY,
            });
          }
        });
      }
    };

    const syncData = debounce(() => {
      if (isTestEnv()) {
        return;
      }
      if (authUserRef.current) {
        return;
      }
      if (
        !document.hidden &&
        ((collabAPI && !collabAPI.isCollaborating()) || isCollabDisabled)
      ) {
        const activeWorkFile = getActiveWorkFileId();
        const workFileKeys = getWorkFileStorageKeys(activeWorkFile);

        // don't sync if local state is newer or identical to browser state
        if (isBrowserStorageStateNewer(workFileKeys.versionDataState)) {
          const localDataState = importFromLocalStorage();
          const username = importUsernameFromLocalStorage();
          setLangCode(getPreferredLanguage());
          excalidrawAPI.updateScene({
            ...localDataState,
            captureUpdate: CaptureUpdateAction.NEVER,
          });
          LibraryIndexedDBAdapter.load().then((data) => {
            if (data) {
              excalidrawAPI.updateLibrary({
                libraryItems: data.libraryItems,
              });
            }
          });
          collabAPI?.setUsername(username || "");
        }

        if (isBrowserStorageStateNewer(STORAGE_KEYS.VERSION_FILES)) {
          const elements = excalidrawAPI.getSceneElementsIncludingDeleted();
          const currFiles = excalidrawAPI.getFiles();
          const fileIds =
            elements?.reduce((acc, element) => {
              if (
                isInitializedImageElement(element) &&
                // only load and update images that aren't already loaded
                !currFiles[element.fileId]
              ) {
                return acc.concat(element.fileId);
              }
              return acc;
            }, [] as FileId[]) || [];
          if (fileIds.length) {
            LocalData.fileStorage
              .getFiles(fileIds)
              .then(({ loadedFiles, erroredFiles }) => {
                if (loadedFiles.length) {
                  excalidrawAPI.addFiles(loadedFiles);
                }
                updateStaleImageStatuses({
                  excalidrawAPI,
                  erroredFiles,
                  elements: excalidrawAPI.getSceneElementsIncludingDeleted(),
                });
              });
          }
        }
      }
    }, SYNC_BROWSER_TABS_TIMEOUT);

    const onUnload = () => {
      LocalData.flushSave();
    };

    const visibilityChange = (event: FocusEvent | Event) => {
      if (event.type === EVENT.BLUR || document.hidden) {
        LocalData.flushSave();
      }
      if (
        event.type === EVENT.VISIBILITY_CHANGE ||
        event.type === EVENT.FOCUS
      ) {
        syncData();
      }
    };

    window.addEventListener(EVENT.HASHCHANGE, onHashChange, false);
    window.addEventListener(EVENT.UNLOAD, onUnload, false);
    window.addEventListener(EVENT.BLUR, visibilityChange, false);
    document.addEventListener(EVENT.VISIBILITY_CHANGE, visibilityChange, false);
    window.addEventListener(EVENT.FOCUS, visibilityChange, false);
    return () => {
      window.removeEventListener(EVENT.HASHCHANGE, onHashChange, false);
      window.removeEventListener(EVENT.UNLOAD, onUnload, false);
      window.removeEventListener(EVENT.BLUR, visibilityChange, false);
      window.removeEventListener(EVENT.FOCUS, visibilityChange, false);
      document.removeEventListener(
        EVENT.VISIBILITY_CHANGE,
        visibilityChange,
        false,
      );
    };
  }, [isCollabDisabled, collabAPI, excalidrawAPI, setLangCode, loadImages]);

  useEffect(() => {
    const unloadHandler = (event: BeforeUnloadEvent) => {
      LocalData.flushSave();

      if (
        excalidrawAPI &&
        LocalData.fileStorage.shouldPreventUnload(
          excalidrawAPI.getSceneElements(),
        )
      ) {
        if (import.meta.env.VITE_APP_DISABLE_PREVENT_UNLOAD !== "true") {
          preventUnload(event);
        } else {
          console.warn(
            "preventing unload disabled (VITE_APP_DISABLE_PREVENT_UNLOAD)",
          );
        }
      }
    };
    window.addEventListener(EVENT.BEFORE_UNLOAD, unloadHandler);
    return () => {
      window.removeEventListener(EVENT.BEFORE_UNLOAD, unloadHandler);
    };
  }, [excalidrawAPI]);

  const onChange = (
    elements: readonly OrderedExcalidrawElement[],
    appState: AppState,
    files: BinaryFiles,
  ) => {
    if (collabAPI?.isCollaborating()) {
      collabAPI.syncElements(elements);
    }

    // this check is redundant, but since this is a hot path, it's best
    // not to evaludate the nested expression every time
    if (!LocalData.isSavePaused()) {
      LocalData.save(
        elements,
        appState,
        files,
        activeWorkFileIdRef.current,
        () => {
          if (excalidrawAPI) {
            let didChange = false;

            const elements = excalidrawAPI
              .getSceneElementsIncludingDeleted()
              .map((element) => {
                if (
                  LocalData.fileStorage.shouldUpdateImageElementStatus(element)
                ) {
                  const newElement = newElementWith(element, {
                    status: "saved",
                  });
                  if (newElement !== element) {
                    didChange = true;
                  }
                  return newElement;
                }
                return element;
              });

            if (didChange) {
              excalidrawAPI.updateScene({
                elements,
                captureUpdate: CaptureUpdateAction.NEVER,
              });
            }
          }
        },
      );
    }

    // Render the debug scene if the debug canvas is available
    if (debugCanvasRef.current && excalidrawAPI) {
      debugRenderer(
        debugCanvasRef.current,
        appState,
        elements,
        window.devicePixelRatio,
      );
    }
  };

  const onExportToBackend = async (
    exportedElements: readonly NonDeletedExcalidrawElement[],
    appState: Partial<AppState>,
    files: BinaryFiles,
  ) => {
    if (exportedElements.length === 0) {
      throw new Error(t("alerts.cannotExportEmptyCanvas"));
    }
    try {
      const { url, errorMessage } = await exportToBackend(
        exportedElements,
        {
          ...appState,
          viewBackgroundColor: appState.exportBackground
            ? appState.viewBackgroundColor
            : getDefaultAppState().viewBackgroundColor,
        },
        files,
      );

      if (errorMessage) {
        throw new Error(errorMessage);
      }

      if (url) {
        setLatestShareableLink(url);
      }
    } catch (error: any) {
      if (error.name !== "AbortError") {
        const { width, height } = appState;
        console.error(error, {
          width,
          height,
          devicePixelRatio: window.devicePixelRatio,
        });
        throw new Error(error.message);
      }
    }
  };

  const renderCustomStats = (
    elements: readonly NonDeletedExcalidrawElement[],
    appState: UIAppState,
  ) => {
    return (
      <CustomStats
        setToast={(message) => excalidrawAPI!.setToast({ message })}
        appState={appState}
        elements={elements}
      />
    );
  };

  const isOffline = useAtomValue(isOfflineAtom);

  const localStorageQuotaExceeded = useAtomValue(localStorageQuotaExceededAtom);

  const onCollabDialogOpen = useCallback(
    () => setShareDialogState({ isOpen: true, type: "collaborationOnly" }),
    [setShareDialogState],
  );

  const openWorkFilesDialog = useCallback(() => {
    void refreshWorkFiles();
    setIsWorkFilesDialogOpen(true);
  }, [refreshWorkFiles]);

  const openAuthDialog = useCallback(() => {
    setIsAuthDialogOpen(true);
  }, []);

  // ---------------------------------------------------------------------------
  // onExport — intercepts file save to wait for pending image loads
  // ---------------------------------------------------------------------------
  const onExport: Required<ExcalidrawProps>["onExport"] = useCallback(
    async function* () {
      let snapshot = FileStatusStore.getSnapshot();
      const { pending, total } = FileStatusStore.getPendingCount(
        snapshot.value,
      );
      if (pending === 0) {
        return;
      }

      // Yield initial progress
      yield {
        type: "progress",
        progress: (total - pending) / total,
        message: `Loading images (${total - pending}/${total})...`,
      };

      // Wait for all pending images to finish
      while (true) {
        snapshot = await FileStatusStore.pull(snapshot.version);
        const { pending: nowPending, total: nowTotal } =
          FileStatusStore.getPendingCount(snapshot.value);

        yield {
          type: "progress",
          progress: (nowTotal - nowPending) / nowTotal,
          message: `Loading images (${nowTotal - nowPending}/${nowTotal})...`,
        };

        if (nowPending === 0) {
          await new Promise((r) => setTimeout(r, 500));
          yield {
            type: "progress",
            message: `Preparing export...`,
          };
          return;
        }
      }
    },
    [],
  );

  // const onExport = () => {
  //   return new Promise((r) => setTimeout(r, 2500));
  //   // console.log("onExport");
  // };

  // browsers generally prevent infinite self-embedding, there are
  // cases where it still happens, and while we disallow self-embedding
  // by not whitelisting our own origin, this serves as an additional guard
  if (isSelfEmbedding) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          height: "100%",
        }}
      >
        <h1>I'm not a pretzel!</h1>
      </div>
    );
  }

  const ExcalidrawPlusCommand = {
    label: "Excalidraw+",
    category: DEFAULT_CATEGORIES.links,
    predicate: true,
    icon: <div style={{ width: 14 }}>{ExcalLogo}</div>,
    keywords: ["plus", "cloud", "server"],
    perform: () => {
      window.open(
        `${
          import.meta.env.VITE_APP_PLUS_LP
        }/plus?utm_source=excalidraw&utm_medium=app&utm_content=command_palette`,
        "_blank",
      );
    },
  };
  const ExcalidrawPlusAppCommand = {
    label: "Sign up",
    category: DEFAULT_CATEGORIES.links,
    predicate: true,
    icon: <div style={{ width: 14 }}>{ExcalLogo}</div>,
    keywords: [
      "excalidraw",
      "plus",
      "cloud",
      "server",
      "signin",
      "login",
      "signup",
    ],
    perform: () => {
      window.open(
        `${
          import.meta.env.VITE_APP_PLUS_APP
        }?utm_source=excalidraw&utm_medium=app&utm_content=command_palette`,
        "_blank",
      );
    },
  };

  return (
    <div
      style={{ height: "100%" }}
      className={clsx("excalidraw-app", {
        "is-collaborating": isCollaborating,
      })}
    >
      <Excalidraw
        onChange={onChange}
        onExport={onExport}
        initialData={initialStatePromiseRef.current.promise}
        isCollaborating={isCollaborating}
        onPointerUpdate={collabAPI?.onPointerUpdate}
        UIOptions={{
          canvasActions: {
            toggleTheme: true,
            export: {
              onExportToBackend,
              renderCustomUI: excalidrawAPI
                ? (elements, appState, files) => {
                    return (
                      <ExportToExcalidrawPlus
                        elements={elements}
                        appState={appState}
                        files={files}
                        name={excalidrawAPI.getName()}
                        onError={(error) => {
                          excalidrawAPI?.updateScene({
                            appState: {
                              errorMessage: error.message,
                            },
                          });
                        }}
                        onSuccess={() => {
                          excalidrawAPI.updateScene({
                            appState: { openDialog: null },
                          });
                        }}
                      />
                    );
                  }
                : undefined,
            },
          },
        }}
        langCode={langCode}
        renderCustomStats={renderCustomStats}
        detectScroll={false}
        handleKeyboardGlobally={true}
        autoFocus={true}
        theme={editorTheme}
        renderTopRightUI={(isMobile) => {
          if (isMobile || !collabAPI || isCollabDisabled) {
            return null;
          }

          return (
            <div className="excalidraw-ui-top-right">
              {excalidrawAPI?.getEditorInterface().formFactor === "desktop" && (
                <ExcalidrawPlusPromoBanner
                  isSignedIn={isExcalidrawPlusSignedUser}
                />
              )}

              {collabError.message && <CollabError collabError={collabError} />}
              <LiveCollaborationTrigger
                isCollaborating={isCollaborating}
                onSelect={() =>
                  setShareDialogState({ isOpen: true, type: "share" })
                }
                editorInterface={editorInterface}
              />
            </div>
          );
        }}
        onLinkOpen={(element, event) => {
          if (element.link && isElementLink(element.link)) {
            event.preventDefault();
            excalidrawAPI?.scrollToContent(element.link, { animate: true });
          }
        }}
      >
        <AppMainMenu
          onCollabDialogOpen={onCollabDialogOpen}
          isCollaborating={isCollaborating}
          isCollabEnabled={!isCollabDisabled}
          theme={appTheme}
          setTheme={(theme) => setAppTheme(theme)}
          refresh={() => forceRefresh((prev) => !prev)}
          onWorkFilesDialogOpen={openWorkFilesDialog}
          onAuthDialogOpen={openAuthDialog}
          authUser={authUser}
        />
        <AppWelcomeScreen
          onCollabDialogOpen={onCollabDialogOpen}
          isCollabEnabled={!isCollabDisabled}
        />
        <OverwriteConfirmDialog>
          <OverwriteConfirmDialog.Actions.ExportToImage />
          <OverwriteConfirmDialog.Actions.SaveToDisk />
          {excalidrawAPI && (
            <OverwriteConfirmDialog.Action
              title={t("overwriteConfirm.action.excalidrawPlus.title")}
              actionLabel={t("overwriteConfirm.action.excalidrawPlus.button")}
              onClick={() => {
                exportToExcalidrawPlus(
                  excalidrawAPI.getSceneElements(),
                  excalidrawAPI.getAppState(),
                  excalidrawAPI.getFiles(),
                  excalidrawAPI.getName(),
                );
              }}
            >
              {t("overwriteConfirm.action.excalidrawPlus.description")}
            </OverwriteConfirmDialog.Action>
          )}
        </OverwriteConfirmDialog>
        <AppFooter onChange={() => excalidrawAPI?.refresh()} />
        {excalidrawAPI && <AIComponents excalidrawAPI={excalidrawAPI} />}
        {isAuthDialogOpen && (
          <UserSessionDialog
            user={authUser}
            onCloseRequest={() => setIsAuthDialogOpen(false)}
            onSignedIn={(user, opts) => {
              authTransitionModeRef.current = opts.isNewAccount
                ? "register"
                : "login";
              setAuthUser(user);
              setIsAuthDialogOpen(false);
            }}
            onSignedOut={() => {
              setAuthUser(null);
            }}
          />
        )}
        {isWorkFilesDialogOpen && (
          <WorkFilesDialog
            activeWorkFileId={activeWorkFileId}
            isRemote={isRemoteMode}
            username={authUser?.username || null}
            workFiles={workFiles}
            onCloseRequest={() => setIsWorkFilesDialogOpen(false)}
            onCreate={createAndSwitchToWorkFile}
            onOpen={switchToWorkFile}
            onRename={renameAndRefreshWorkFile}
            onDelete={deleteAndRefreshWorkFile}
          />
        )}

        <TTDDialogTrigger />
        {isCollaborating && isOffline && (
          <div className="alertalert--warning">
            {t("alerts.collabOfflineWarning")}
          </div>
        )}
        {localStorageQuotaExceeded && (
          <div className="alert alert--danger">
            {t("alerts.localStorageQuotaExceeded")}
          </div>
        )}
        {latestShareableLink && (
          <ShareableLinkDialog
            link={latestShareableLink}
            onCloseRequest={() => setLatestShareableLink(null)}
            setErrorMessage={setErrorMessage}
          />
        )}
        {excalidrawAPI && !isCollabDisabled && (
          <Collab excalidrawAPI={excalidrawAPI} />
        )}

        <ShareDialog
          collabAPI={collabAPI}
          onExportToBackend={async () => {
            if (excalidrawAPI) {
              try {
                await onExportToBackend(
                  excalidrawAPI.getSceneElements(),
                  excalidrawAPI.getAppState(),
                  excalidrawAPI.getFiles(),
                );
              } catch (error: any) {
                setErrorMessage(error.message);
              }
            }
          }}
        />

        <AppSidebar />

        {errorMessage && (
          <ErrorDialog onClose={() => setErrorMessage("")}>
            {errorMessage}
          </ErrorDialog>
        )}

        <CommandPalette
          customCommandPaletteItems={[
            {
              label: t("labels.liveCollaboration"),
              category: DEFAULT_CATEGORIES.app,
              keywords: [
                "team",
                "multiplayer",
                "share",
                "public",
                "session",
                "invite",
              ],
              icon: usersIcon,
              perform: () => {
                setShareDialogState({
                  isOpen: true,
                  type: "collaborationOnly",
                });
              },
            },
            {
              label: t("roomDialog.button_stopSession"),
              category: DEFAULT_CATEGORIES.app,
              predicate: () => !!collabAPI?.isCollaborating(),
              keywords: [
                "stop",
                "session",
                "end",
                "leave",
                "close",
                "exit",
                "collaboration",
              ],
              perform: () => {
                if (collabAPI) {
                  collabAPI.stopCollaboration();
                  if (!collabAPI.isCollaborating()) {
                    setShareDialogState({ isOpen: false });
                  }
                }
              },
            },
            {
              label: t("labels.share"),
              category: DEFAULT_CATEGORIES.app,
              predicate: true,
              icon: share,
              keywords: [
                "link",
                "shareable",
                "readonly",
                "export",
                "publish",
                "snapshot",
                "url",
                "collaborate",
                "invite",
              ],
              perform: async () => {
                setShareDialogState({ isOpen: true, type: "share" });
              },
            },
            {
              label: "GitHub",
              icon: GithubIcon,
              category: DEFAULT_CATEGORIES.links,
              predicate: true,
              keywords: [
                "issues",
                "bugs",
                "requests",
                "report",
                "features",
                "social",
                "community",
              ],
              perform: () => {
                window.open(
                  "https://github.com/excalidraw/excalidraw",
                  "_blank",
                  "noopener noreferrer",
                );
              },
            },
            {
              label: t("labels.followUs"),
              icon: XBrandIcon,
              category: DEFAULT_CATEGORIES.links,
              predicate: true,
              keywords: ["twitter", "contact", "social", "community"],
              perform: () => {
                window.open(
                  "https://x.com/excalidraw",
                  "_blank",
                  "noopener noreferrer",
                );
              },
            },
            {
              label: t("labels.discordChat"),
              category: DEFAULT_CATEGORIES.links,
              predicate: true,
              icon: DiscordIcon,
              keywords: [
                "chat",
                "talk",
                "contact",
                "bugs",
                "requests",
                "report",
                "feedback",
                "suggestions",
                "social",
                "community",
              ],
              perform: () => {
                window.open(
                  "https://discord.gg/UexuTaE",
                  "_blank",
                  "noopener noreferrer",
                );
              },
            },
            {
              label: "YouTube",
              icon: youtubeIcon,
              category: DEFAULT_CATEGORIES.links,
              predicate: true,
              keywords: ["features", "tutorials", "howto", "help", "community"],
              perform: () => {
                window.open(
                  "https://youtube.com/@excalidraw",
                  "_blank",
                  "noopener noreferrer",
                );
              },
            },
            ...(isExcalidrawPlusSignedUser
              ? [
                  {
                    ...ExcalidrawPlusAppCommand,
                    label: "Sign in / Go to Excalidraw+",
                  },
                ]
              : [ExcalidrawPlusCommand, ExcalidrawPlusAppCommand]),

            {
              label: t("overwriteConfirm.action.excalidrawPlus.button"),
              category: DEFAULT_CATEGORIES.export,
              icon: exportToPlus,
              predicate: true,
              keywords: ["plus", "export", "save", "backup"],
              perform: () => {
                if (excalidrawAPI) {
                  exportToExcalidrawPlus(
                    excalidrawAPI.getSceneElements(),
                    excalidrawAPI.getAppState(),
                    excalidrawAPI.getFiles(),
                    excalidrawAPI.getName(),
                  );
                }
              },
            },
            {
              ...CommandPalette.defaultItems.toggleTheme,
              perform: () => {
                setAppTheme(
                  editorTheme === THEME.DARK ? THEME.LIGHT : THEME.DARK,
                );
              },
            },
            {
              label: t("labels.installPWA"),
              category: DEFAULT_CATEGORIES.app,
              predicate: () => !!pwaEvent,
              perform: () => {
                if (pwaEvent) {
                  pwaEvent.prompt();
                  pwaEvent.userChoice.then(() => {
                    // event cannot be reused, but we'll hopefully
                    // grab new one as the event should be fired again
                    pwaEvent = null;
                  });
                }
              },
            },
          ]}
        />
        {isVisualDebuggerEnabled() && excalidrawAPI && (
          <DebugCanvas
            appState={excalidrawAPI.getAppState()}
            scale={window.devicePixelRatio}
            ref={debugCanvasRef}
          />
        )}
      </Excalidraw>
    </div>
  );
};

const ExcalidrawApp = () => {
  const isCloudExportWindow =
    window.location.pathname === "/excalidraw-plus-export";
  if (isCloudExportWindow) {
    return <ExcalidrawPlusIframeExport />;
  }

  return (
    <TopErrorBoundary>
      <Provider store={appJotaiStore}>
        <ExcalidrawAPIProvider>
          <ExcalidrawWrapper />
        </ExcalidrawAPIProvider>
      </Provider>
    </TopErrorBoundary>
  );
};

export default ExcalidrawApp;
